import tokens from '@nimbus-ds/tokens/dist/js/tokens';
import { StylesConfig } from 'react-select';

export interface OptionType {
  value: string;
  label: string;
}

export const colourStyles = (
  aiGenerated: string[],
  hasError = false,
  duplicateTags: string[] = [],
): StylesConfig<OptionType> => ({
  control: (styles, { isDisabled, isFocused }) => {
    const errorBorderColor = hasError
      ? 'var(--nimbus-colors-danger-interactive)'
      : tokens.color.light.neutral[80].value;
    const focusBorderColor = hasError
      ? 'var(--nimbus-colors-danger-interactive)'
      : tokens.color.dark.primary['interactive-pressed'].value;
    const hoverBorderColor = hasError
      ? 'var(--nimbus-colors-danger-interactive)'
      : tokens.color.light.neutral[80].value;

    const getBackgroundColor = () => {
      if (isDisabled) {
        return 'var(--nimbus-colors-neutral-surfaceDisabled)';
      }
      if (hasError) {
        return 'var(--nimbus-colors-danger-surface)';
      }
      return undefined;
    };

    return {
      ...styles,
      borderColor: isFocused ? focusBorderColor : errorBorderColor,
      borderRadius: tokens.shape.border.radius[2].value,
      fontWeight: tokens.font.weight.regular.value,
      fontSize: tokens.font.size.body.base.value,
      fontFamily: tokens.font.family.sans.value,
      backgroundColor: getBackgroundColor(),
      cursor: isDisabled ? 'not-allowed' : 'default',
      ':hover': {
        borderColor: hoverBorderColor,
      },
      ':focus': {
        borderColor: focusBorderColor,
      },
      boxShadow: tokens.shadow.level[0].value,
    };
  },
  option: (styles, { isFocused, isSelected, isDisabled }) => ({
    ...styles,
    fontSize: tokens.font.size.body.base.value,
    fontFamily: tokens.font.family.sans.value,
    backgroundColor:
      isSelected || isFocused
        ? tokens.color.light.neutral[99].value
        : undefined,
    color: 'blue',
    opacity: isDisabled ? 0.5 : 1,
    cursor: 'pointer',
  }),
  multiValue: (styles, { data }) => {
    const isDuplicate = duplicateTags.includes(data.value);
    const isAiGenerated =
      aiGenerated.length > 0 && aiGenerated.includes(data.value);

    const baseStyles = {
      display: 'flex',
      alignItems: 'center',
      margin: '2px 4px 2px 0',
      borderStyle: 'solid',
      borderRadius: tokens.shape.border.radius[1].value,
      borderWidth: tokens.shape.border.width[1].value,
      color: tokens.color.light.neutral[0].value,
      cursor: 'pointer',
      height: tokens.line.height[6].value,
    };

    if (isDuplicate) {
      return {
        ...styles,
        ...baseStyles,
        backgroundColor: 'var(--nimbus-colors-danger-surface)',
        borderColor: 'var(--nimbus-colors-danger-interactive)',
      };
    }

    return {
      ...styles,
      ...baseStyles,
      ...(isAiGenerated
        ? {
            backgroundColor: tokens.color.light.neutral[99].value,
            transition: 'box-shadow var(--nimbus-motion-speed-fast) ease',
            boxShadow: 'var(--nimbus-utils-aiFocus)',
            background:
              'linear-gradient(var(--nimbus-colors-neutral-surface), var(--nimbus-colors-neutral-surface)) padding-box, linear-gradient(93deg, rgba(0, 80, 195, 1) 5%, rgba(71, 54, 180, 1) 40%, rgba(216, 68, 110, 1) 95%) border-box',
            borderColor: 'transparent',
          }
        : {
            backgroundColor: tokens.color.light.neutral[99].value,
            borderColor: tokens.color.light.neutral[80].value,
          }),
    };
  },
  multiValueLabel: (styles, { isDisabled, data }) => ({
    ...styles,
    ...(aiGenerated.length > 0 && aiGenerated.includes(data.value)
      ? {
          fontFamily: 'var(--nimbus-fontFamily-sans)',
          fontSize: 'var(--nimbus-fontSize-body-caption)',
          padding: '2px 6px',
          display: 'flex',
          alignItems: 'center',
          fontWeight: 500,
        }
      : {
          fontFamily: tokens.font.family.sans.value,
          fontSize: tokens.font.size.body.caption.value,
          padding: '2px 6px',
          display: 'flex',
          alignItems: 'center',
          opacity: isDisabled ? 0.8 : 1,
        }),
  }),
  multiValueRemove: (styles, { isDisabled, data }) => ({
    ...styles,
    ...(aiGenerated.length > 0 && aiGenerated.includes(data.value)
      ? {
          display: isDisabled ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 4px',
          height: '100%',
          ':hover': isDisabled
            ? {}
            : {
                backgroundColor: 'transparent',
                borderColor: 'transparent',
              },
        }
      : {
          display: isDisabled ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 4px',
          height: '100%',
          ':hover': isDisabled
            ? {}
            : {
                backgroundColor: tokens.color.light.neutral[90].original.value,
                borderColor: tokens.color.light.neutral[70].value,
              },
        }),
  }),
  noOptionsMessage: (styles) => ({
    ...styles,
    fontSize: tokens.font.size.body.base.value,
    fontFamily: tokens.font.family.sans.value,
  }),
  dropdownIndicator: (prevStyle) => ({
    ...prevStyle,
    display: 'none',
  }),
  indicatorSeparator: (prevStyle) => ({
    ...prevStyle,
    display: 'none',
  }),
});
